import { apiGet, apiPost, apiPatch, apiPut, apiDelete } from './client';
import type { LeadStage, LeadStageKind } from './leadStages';

export type { LeadStage, LeadStageKind };

export interface LeadOwner {
  id: number;
  firstName: string | null;
  lastName: string | null;
}

export interface LeadProject {
  id: number;
  name: string | null;
}

export interface LeadCustomer {
  id: number;
  name: string | null;
}

export interface PaymentTerm {
  id: number;
  name: string;
  days: number;
}

/**
 * MKS-94 — billing type: a row of the backend PaymentType registry (T&M, Fixed
 * Price, SLA balíček). The legacy `Default` template is filtered out server-side
 * and is never offered on a lead.
 */
export interface BillingType {
  id: number;
  name: string;
  slug: string;
}

/** MKS-94 — how billing periods are formed. */
export const LEAD_BILLING_FREQUENCIES = ['monthly', 'quarterly', 'per_phase'] as const;
export type LeadBillingFrequency = (typeof LEAD_BILLING_FREQUENCIES)[number];

export const BILLING_FREQUENCY_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  per_phase: 'Per phase',
  // Project-only cadences, kept here so the project form can label a value it
  // inherited from the SLA flow (see backend services/billingTerms.ts).
  yearly: 'Yearly',
  one_off: 'One-off',
  perpetual: 'Perpetual',
};

export interface LeadLabel {
  id: number;
  name: string;
  color: string;
}

export interface LeadSource {
  id: number;
  name: string;
}

/** MKS-347 — one entry of the configurable "why did we lose it?" list. */
export interface LeadLostReason {
  id: number;
  name: string;
}

/**
 * MKS-344 — a reusable contact as it appears ON a lead. `id` is the Contact's
 * id (not the join row's), so it is what the picker and `/sales/contacts`
 * key on. The list is primary-first, so `contacts[0]` is the primary when
 * there is one.
 */
export interface LeadContact {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  customerId: number | null;
  customer: LeadCustomer | null;
  isPrimary: boolean;
}

/** MKS-344 — what a lead write sends: links only, never contact fields. */
export interface LeadContactInput {
  contactId: number;
  isPrimary?: boolean;
}

/** The primary contact of a lead, or null when it has none. */
export function primaryContact(lead: Pick<Lead, 'contacts'>): LeadContact | null {
  return lead.contacts?.find((c) => c.isPrimary) ?? lead.contacts?.[0] ?? null;
}

/**
 * MKS-346 — one SLA pricing configuration linked to a lead. `id` is the
 * CONFIGURATION's id, which is what `/sales/sla-pricing/:id` keys on, not the
 * join row's.
 *
 * `monthlyPrice` is a fixed-2 decimal STRING, never a number: the backend
 * computes it in Decimal space and emits it as text so no float
 * representation ever reaches the browser (T-107-MONEYWIRE). `currency` is
 * the configuration's own and is never converted or summed across rows (D-14).
 */
export interface LeadSlaPricingConfiguration {
  id: number;
  name: string;
  currency: string;
  monthlyPrice: string;
}

export interface Lead {
  id: number;
  name: string;
  customer: LeadCustomer;
  // MKS-344 — superseded by `contacts` below; kept for one release so the
  // flag-off branch keeps today's three text inputs working.
  contactPerson: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  dealValue: string | null;
  dealValueCurrency: string;
  paymentTermId: number | null;
  paymentTerm: PaymentTerm | null;
  // MKS-94 — billing terms. `billingTypeId` is independent of `paymentTermId`:
  // one is what we bill (T&M / Fixed Price / retainer), the other when it's due.
  billingTypeId: number | null;
  billingType: BillingType | null;
  billingFrequency: string | null;
  contractUrl: string | null;
  sourceId: number | null;
  source: LeadSource | null;
  // MKS-347 — why the deal was lost. Null on every open or won lead; written
  // when the lead enters a lost stage and cleared when it leaves one.
  lostReasonId: number | null;
  lostReason: LeadLostReason | null;
  lostReasonNote: string | null;
  // MKS-345 — `stage` is the legacy slug, kept for one release. Everything
  // that used to compare it against 'won' / 'lost' now reads `stageKind`, the
  // board groups its columns on `stageId`, and the weighted column total
  // multiplies by `stageProbability`.
  stage: string;
  stageId: number;
  stageName: string;
  stageColor: string;
  stageKind: LeadStageKind;
  stageProbability: number;
  expectedCloseDate: string | null;
  expectedStartDate: string | null;
  expectedEndDate: string | null;
  stageChangedAt: string;
  // MKS-294 — max(stage change, lead edit, note, role change), computed by
  // the backend. The one clock every board's stale marker counts from.
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
  owner: LeadOwner;
  convertedProject: LeadProject | null;
  nonBillableProject: LeadProject | null;
  presalesProject: LeadProject | null;
  leadRoles: LeadRoleEntry[];
  leadNotes: LeadNoteEntry[];
  labels: LeadLabel[];
  // MKS-344 — reusable contacts on this lead, primary first.
  contacts: LeadContact[];
  // MKS-346 — the SLA pricing configurations priced for this deal.
  slaPricingConfigurations: LeadSlaPricingConfiguration[];
}

export interface LeadNoteEntry {
  id: number;
  content: string;
  createdAt: string;
  author: LeadOwner;
}

export interface LeadRoleEntry {
  id: number;
  headcount: number;
  hourlyRate: number | null;
  currency: string;
  role: { id: number; name: string; code: string | null };
}

export interface StageTotals {
  // F13-003: `value` is a per-currency breakdown (e.g. { CZK: 800000, EUR: 120000 })
  // — never sum across currencies on the backend.
  //
  // MKS-345 — keyed by `String(stage.id)`, not by the retired stage slug, and
  // carrying `weightedValue`: the same map scaled by the stage's win
  // probability, which the column header renders beside the raw total.
  [stageId: string]: {
    count: number;
    value: Record<string, number>;
    weightedValue: Record<string, number>;
  };
}

export interface LeadListResponse {
  data: Lead[];
  total: number;
  // MKS-345 — the configured pipeline, in board order. Travels with the leads
  // so the board renders itself from this one request.
  stages: LeadStage[];
  stageTotals: StageTotals;
}

export interface LeadRoleInput {
  roleId: number;
  headcount: number;
  hourlyRate?: number | null;
  currency?: string;
}

export interface CreateLeadInput {
  name: string;
  customerId: number;
  contactPerson?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  dealValue?: number | null;
  dealValueCurrency?: string;
  paymentTermId?: number | null;
  // MKS-94 — billing terms.
  billingTypeId?: number | null;
  billingFrequency?: string | null;
  contractUrl?: string | null;
  sourceId?: number | null;
  // MKS-345 — the pipeline reference. `stage` (a name or the legacy slug) is
  // still accepted by the API but the SPA always sends the id.
  stageId?: number;
  expectedCloseDate?: string | null;
  expectedStartDate?: string | null;
  expectedEndDate?: string | null;
  leadRoles?: LeadRoleInput[];
  labelIds?: number[];
  // MKS-344 — replace-all set of contact links, like `labelIds`. Omit the key
  // to leave the lead's contacts untouched.
  contacts?: LeadContactInput[];
  // MKS-346 — the SLA pricing configurations the lead should end up linked to
  // (replace-all, like `labelIds`). Omitted entirely while the release flag is
  // off, which leaves the lead's existing links untouched.
  slaPricingConfigurationIds?: number[];
}

export interface UpdateLeadInput {
  name?: string;
  customerId?: number;
  contactPerson?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  dealValue?: number | null;
  dealValueCurrency?: string;
  paymentTermId?: number | null;
  // MKS-94 — billing terms.
  billingTypeId?: number | null;
  billingFrequency?: string | null;
  contractUrl?: string | null;
  sourceId?: number | null;
  // MKS-345 — see CreateLeadInput.
  stageId?: number;
  expectedCloseDate?: string | null;
  expectedStartDate?: string | null;
  expectedEndDate?: string | null;
  ownerId?: number;
  convertedProjectId?: number | null;
  nonBillableProjectId?: number | null;
  leadRoles?: LeadRoleInput[];
  labelIds?: number[];
  // MKS-344 — replace-all set of contact links, like `labelIds`. Omit the key
  // to leave the lead's contacts untouched.
  contacts?: LeadContactInput[];
  // MKS-346 — the SLA pricing configurations the lead should end up linked to
  // (replace-all, like `labelIds`). Omitted entirely while the release flag is
  // off, which leaves the lead's existing links untouched.
  slaPricingConfigurationIds?: number[];
  // MKS-347 — required by the backend when this edit moves the lead into a
  // stage of kind 'lost'.
  lostReasonId?: number | null;
  lostReasonNote?: string | null;
}

export async function fetchLeads(): Promise<LeadListResponse> {
  return apiGet('/leads');
}

export async function fetchLead(id: number): Promise<{ data: Lead }> {
  return apiGet(`/leads/${id}`);
}

export async function createLead(input: CreateLeadInput): Promise<{ data: Lead }> {
  return apiPost('/leads', input);
}

export async function updateLead(id: number, input: UpdateLeadInput): Promise<{ data: Lead }> {
  return apiPatch(`/leads/${id}`, input);
}

export async function updateLeadStage(id: number, stageId: number): Promise<{ data: Lead }> {
  return apiPatch(`/leads/${id}/stage`, { stageId });
}

// MKS-213 — drag-and-drop reorder within (or across) a stage column.
export interface ReorderLeadInput {
  // MKS-345 — the target column, by id.
  stageId: number;
  beforeLeadId?: number | null;
  afterLeadId?: number | null;
  // MKS-347 — a drag into the lost column carries the reason with it, because
  // that drag IS how a deal is usually marked lost.
  lostReasonId?: number | null;
  lostReasonNote?: string | null;
}

export async function reorderLead(id: number, input: ReorderLeadInput): Promise<{ data: Lead }> {
  return apiPatch(`/leads/${id}/reorder`, input);
}

export interface ProjectRole {
  id: number;
  name: string;
  code: string | null;
  referenceCount?: number;
}

export async function fetchProjectRoles(): Promise<{ data: ProjectRole[] }> {
  return apiGet('/project-roles');
}

export async function createProjectRole(name: string, code?: string): Promise<{ data: ProjectRole }> {
  return apiPost('/project-roles', { name, ...(code ? { code } : {}) });
}

export async function updateProjectRole(id: number, data: { name?: string; code?: string | null }): Promise<{ data: ProjectRole }> {
  return apiPut(`/project-roles/${id}`, data);
}

export async function deleteProjectRole(id: number): Promise<void> {
  return apiDelete(`/project-roles/${id}`);
}

export async function addLeadNote(leadId: number, content: string): Promise<{ data: LeadNoteEntry }> {
  return apiPost(`/leads/${leadId}/notes`, { content });
}

export async function deleteLeadNote(leadId: number, noteId: number): Promise<void> {
  return apiDelete(`/leads/${leadId}/notes/${noteId}`);
}

export async function deleteLead(id: number): Promise<void> {
  return apiDelete(`/leads/${id}`);
}

// Payment Terms
export async function fetchPaymentTerms(): Promise<{ data: PaymentTerm[] }> {
  return apiGet('/leads/payment-terms');
}

export async function createPaymentTermApi(name: string, days: number): Promise<{ data: PaymentTerm }> {
  return apiPost('/leads/payment-terms', { name, days });
}

export async function updatePaymentTermApi(id: number, data: { name?: string; days?: number }): Promise<{ data: PaymentTerm }> {
  return apiPatch(`/leads/payment-terms/${id}`, data);
}

export async function deletePaymentTermApi(id: number): Promise<void> {
  return apiDelete(`/leads/payment-terms/${id}`);
}

// MKS-94 — billing types for the lead form. Sales-readable, unlike the admin
// template catalogue at /admin/templates/payment-types.
export async function fetchBillingTypes(): Promise<{ data: BillingType[] }> {
  return apiGet('/leads/billing-types');
}

// Lead Labels catalog
export async function fetchLeadLabels(): Promise<{ data: LeadLabel[] }> {
  return apiGet('/leads/labels');
}

export async function createLeadLabelApi(name: string, color: string): Promise<{ data: LeadLabel }> {
  return apiPost('/leads/labels', { name, color });
}

// Lead Sources catalog (acquisition channel)
export async function fetchLeadSources(): Promise<{ data: LeadSource[] }> {
  return apiGet('/leads/sources');
}

export async function createLeadSourceApi(name: string): Promise<{ data: LeadSource }> {
  return apiPost('/leads/sources', { name });
}

// MKS-347 — Lost-reason catalog. Readable by sales and marketing, creatable
// by sales, exactly like the sources catalog above.
export async function fetchLeadLostReasons(): Promise<{ data: LeadLostReason[] }> {
  return apiGet('/leads/lost-reasons');
}

export async function createLeadLostReasonApi(name: string): Promise<{ data: LeadLostReason }> {
  return apiPost('/leads/lost-reasons', { name });
}
